import { useMemo, useRef, useState } from "react";
import * as Icons from "react-icons/bs";
import { TDocument, TWindowPosition, Themes } from "../../types";
import useDraggable from "../../hooks/useDraggable";
import { date2DDMMMYYYString } from "../../../lib/textutils";
import '../../scss/lex-doc-rel.scss';
import { useDocuments } from "../../contexts/DekstopContext";

export type TDocumentHistoryData = {
  docid: string,
  lexid: string,
  title: string,
  revisions: TDocument['revisions'],
}

const DocumentHistoryModal = ({
  theme,
  data,
  loading,
  onClose,
}: {
  theme: Themes,
  data?: TDocumentHistoryData,
  loading: boolean,
  onClose: () => void,
}) => {
  const [position, setPosition] = useState<TWindowPosition>({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const [selectedId, setSelectedId] = useState<string>();

  const { openDocument } = useDocuments();

  const { isDragging, handleMouseDown } = useDraggable({
    initialPosition: position,
    containerRef,
    elementRef: windowRef,
    onPositionChange: (p) => setPosition(p),
    autoAlignToMiddle: true,
  });

  const revisions = useMemo(() => {
    if (!data) return [];
    return [...data.revisions]
      .sort((a, b) => new Date(b.rev_date).getTime() - new Date(a.rev_date).getTime());
  }, [data]);

  const openRevision = (revisionId: string) => {
    if (!data) return;
    openDocument({ id: revisionId, title: data.title });
    onClose();
  };

  return (
    <div className="full-screen-modal" ref={containerRef}>
      <div
        className={`virtual-window advanced-search-container  ${isDragging ? "dragging" : ""} ${theme}`}
        ref={windowRef}
        style={{
          position: "absolute",
          top: `${position.y}px`,
          left: `${position.x}px`,
          height: '50vh',
        }}
      >
        <div className="window-header" onMouseDown={handleMouseDown}>
          <div className="window-title">
            <i className="file-icon">
              <Icons.BsClockHistory />
            </i>
            <span>Istoric act</span>
          </div>
          <div className="window-controls">
            <button className="window-control close" onClick={() => onClose()}>
              ✕
            </button>
          </div>
        </div>
        <div className="window-content">
          {
            loading
              ? <div className='lex-secondary-loading'>Se incarca ...</div>
              : <div className='advanced-search-panel-options-inner' style={{ minHeight: 300, width: '100%' }}>
                {
                  revisions.map((rev) => {
                    const isCurrent = rev.id === data?.docid || rev.lexid === data?.lexid;
                    return (
                      <div
                        key={`rev-${rev.id}`}
                        className={`lex-doc-rel ${selectedId === rev.id ? 'selected' : ''}`}
                        style={{ fontWeight: isCurrent ? 'bold' : 'normal' }}
                        onClick={() => setSelectedId(rev.id)}
                        onDoubleClick={() => openRevision(rev.id)}
                      >
                        {rev.rev_type} - {date2DDMMMYYYString(new Date(rev.rev_date))}
                      </div>
                    );
                  })}
              </div>
          }
        </div>
        <div className="window-footer" style={{ justifyContent: 'center' }}>
          <button
            className={`advanced-search-button ${selectedId === undefined ? 'disabled' : ''}`}
            style={{ marginRight: 'unset' }}
            onClick={() => {
              if (selectedId !== undefined) openRevision(selectedId);
            }}
          >
            Afiseaza
          </button>
          <button className="advanced-search-button" style={{ marginLeft: 'unset' }} onClick={onClose}>
            Inchide
          </button>
        </div>
      </div>
    </div>
  )
}

export default DocumentHistoryModal;
